<script setup>
import { Head } from '@inertiajs/vue3'
import { onBeforeUnmount, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

const PRIVACY_POLICY_CONTAINER_ID = 'dcrt_dps_content'
const PRIVACY_POLICY_SCRIPT_SELECTOR = 'script[data-decareto-privacy-policy="true"]'
const PRIVACY_POLICY_SCRIPT_SRC = 'https://decareto.b-cdn.net/p/44e38eaae0dc3d5c814c.js'

const { t, locale } = useI18n()

const loadPrivacyPolicy = () => {
  const contentContainer = document.getElementById(PRIVACY_POLICY_CONTAINER_ID)

  if (!contentContainer) {
    return
  }

  contentContainer.innerHTML = ''
  document.querySelector(PRIVACY_POLICY_SCRIPT_SELECTOR)?.remove()

  const scriptElement = document.createElement('script')
  scriptElement.src = `${PRIVACY_POLICY_SCRIPT_SRC}?t=${Date.now()}`
  scriptElement.dataset.decaretoPrivacyPolicy = 'true'
  document.head.appendChild(scriptElement)
}

onMounted(() => {
  loadPrivacyPolicy()
})

onBeforeUnmount(() => {
  document.querySelector(PRIVACY_POLICY_SCRIPT_SELECTOR)?.remove()
})
</script>

<template>
  <Head :title="t('title')" />
  <Card class="w-full !ring-8 ring-white/20" :dt="{ body: { padding: '4rem', gap: '2rem' }, root: { shadow: 'none' } }">
    <template #title>{{ t('title') }}</template>
    <template #content>
      <div class="flex flex-col gap-4">
        <Message v-if="locale !== 'de'" severity="warn" icon="pi pi-exclamation-circle">
          {{ t('language_notice') }}
        </Message>
        <div :id="PRIVACY_POLICY_CONTAINER_ID" class="privacy-policy-content"></div>
      </div>
    </template>
  </Card>
</template>

<style scoped>
@reference "../../../css/app.css";

.privacy-policy-content :deep(h2) {
  @apply text-surface-900 mt-8 text-lg font-semibold;
}

.privacy-policy-content :deep(p) {
  @apply text-surface-700 text-base leading-6;
}

.privacy-policy-content :deep(ul) {
  @apply text-surface-700 my-4 list-disc space-y-2 pl-6 text-base;
}

.privacy-policy-content :deep(li) {
  @apply leading-6;
}

.privacy-policy-content :deep(strong > em) {
  @apply text-sm font-normal not-italic opacity-70;
}
</style>

<i18n>
{
    "en": {
        "title": "Privacy Policy",
        "language_notice": "This document is only available in German. If you need a translation, please contact us."
    },
    "de": {
        "title": "Datenschutzerklärung",
        "language_notice": "Dieses Dokument ist nur auf Deutsch verfügbar. Wenn Sie eine Übersetzung benötigen, kontaktieren Sie uns bitte."
    }
}
</i18n>
